import { TFunction, useTranslation } from "react-i18next";
import { ValidationFunction } from "@components";
import { useGetInvestments } from "@api/v1/hooks/useInvestments";
import { Investment } from "@api";
import { Values, InvestmentToTransfer } from "../DepotTransferWizard";

const investmentToTransferValidation = (
  investments: Investment[],
  value: unknown,
  t: TFunction<"translation", undefined>
) => {
  if (!value || (value as Array<any>).length === 0) {
    return {
      message: t("depotTransfer.validation.investmentsToTransferIsEmpty"),
    };
  }
  const errors = (value as Array<InvestmentToTransfer>)
    .map(({ id, numberOfLots }) => {
      if (numberOfLots != null && numberOfLots <= 0) {
        return {
          id,
          message: t(
            "depotTransfer.validation.numberOfLotsCanNotBeLessThanZero"
          ),
        };
      }
      const investment = investments.find((investment) => investment.id === id);
      if (
        investment &&
        numberOfLots &&
        numberOfLots > investment?.numberOfLots
      ) {
        return {
          id,
          message: t(
            "depotTransfer.validation.numberOfLotsCanNotBeMoreThanOriginal"
          ),
        };
      }
      return undefined;
    })
    .filter((v) => !!v);
  if (errors && errors.length > 0) {
    return {
      message: t("depotTransfer.validation.investmentsToTransferIsIncorrect"),
      errors,
    };
  }
};

export const useValidation = (accountId: number) => {
  const { t } = useTranslation();
  const { data: investmentsResponse, isFetching } = useGetInvestments({
    accountId: [accountId],
    order: "name asc",
  });
  const investments = investmentsResponse?.investments ?? [];

  const handleValidation: ValidationFunction<Values, keyof Values, never> = ({
    name,
    value,
  }) => {
    switch (name) {
      case "toAccountId":
        if (value == null) {
          return {
            message: t("depotTransfer.validation.destinationAccountIdIsEmpty"),
          };
        }
        return;

      case "dateOfTransfer":
        if (value == null) {
          return {
            message: t("depotTransfer.validation.dateOfTransferCanNotBeEmpty"),
          };
        }

        if ((value as Date) > new Date()) {
          return {
            message: t(
              "depotTransfer.validation.dateOfTransferCanNotBeInFuture"
            ),
          };
        }
        return;

      case "investmentsToTransfer":
        return investmentToTransferValidation(investments, value, t);
      default:
        break;
    }
  };

  return { validate: handleValidation, isFetching };
};
